import { useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import "./PingMapsButton.scss";
import { Flex, Logo } from "@repo/ping-design";
import map from "../../assets/map.png";

import { PLI_URL } from "./Constants";

type PingMapsButtonProps = {
  disabled?: boolean;
};

const PingMapsButton = ({ disabled = false }: PingMapsButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!disabled) {
      setIsHovered(false);
    }
  };

  return (
    <a
      className={`PingMapsButton${disabled ? " PingMapsButton--disabled" : ""}`}
      href={disabled ? undefined : PLI_URL}
      target={disabled ? undefined : "_blank"}
      rel={disabled ? undefined : "noreferrer"}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="PingMapsButton__Content">
        <div className="PingMapsButton__Content__LogoContainer">
          <svg
            viewBox="0 0 70 112"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="PingMapsButton__Content__LogoContainer__Icon"
          >
            <path
              d="M55.1914 76.9999L45.1581 66.9666M50.5249 53.6667C50.5249 63.976 42.1675 72.3334 31.8582 72.3334C21.5489 72.3334 13.1915 63.976 13.1915 53.6667C13.1915 43.3574 21.5489 35 31.8582 35C42.1675 35 50.5249 43.3574 50.5249 53.6667Z"
              stroke="#37384D"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Flex>
            <Logo className="PingMapsButton__Content__LogoContainer__Logo" />
            <p className="PingMapsButton__Content__LogoContainer__Location">
              .Location
            </p>
          </Flex>
        </div>
        <div className="PingMapsButton__Content__Text">
          Explore any Property
        </div>
      </div>
      <div className="PingMapsButton__BgFilter" />
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 580 480"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="PingMapsButton__SVG"
      >
        <g opacity="0.4" filter="url(#filter0_f_340_13067)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M132.711 14.2839C195.316 14.6369 267.931 -62.6566 314.055 -20.3241C365.584 26.9685 294.42 120.614 319.642 185.849C339.005 235.929 418.773 242.751 435.497 293.773C452.488 345.611 430.915 403.095 406.906 452.079C381.974 502.944 346.698 551.22 296.47 577.411C246.944 603.234 188.546 593.622 132.711 595.051C75.4482 596.517 0.215958 630.333 -35.9594 585.919C-77.8899 534.44 2.24648 445.637 -34.1947 390.136C-74.4426 328.838 -205.51 363.64 -227.78 293.773C-246.643 234.595 -139.948 208.069 -109.48 153.944C-78.517 98.9374 -104.499 9.15645 -49.8174 -22.3759C4.87445 -53.9139 69.5788 13.9279 132.711 14.2839Z"
            fill="#A2FF00"
          />
        </g>
        <g opacity="0.5" filter="url(#filter1_f_340_13067)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M378.781 -150.167C429.011 -162.512 471.061 -109.357 516.665 -84.9494C563.843 -59.6988 626.583 -50.9614 652.057 -3.90396C677.548 43.1829 662.259 102.873 645.95 153.872C632.074 197.262 590.667 223.62 566.874 262.468C543.876 300.018 544.618 353.342 508.423 378.419C472.065 403.61 422.922 395.619 378.781 392.787C336.762 390.091 293.915 384.994 258.4 362.379C223.552 340.189 206.818 300.511 182.001 267.483C153.664 229.77 118.492 197.967 101.731 153.872C80.032 96.7894 28.9052 20.2859 71.342 -23.6277C119.376 -73.3327 211.245 0.711838 275.164 -25.5967C325.975 -46.51 325.422 -137.053 378.781 -150.167Z"
            fill="#FD6DBA"
          />
        </g>
        <g opacity="0.3" filter="url(#filter2_f_340_13067)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M449.08 171.313C503.635 184.855 524.227 253.523 573.1 281.293C622.483 309.353 704.968 282.787 732.601 332.411C760.397 382.327 696.987 439.128 692.715 496.102C688.887 547.163 723.571 597.1 709.072 646.209C693.463 699.08 664.191 763.788 610.282 775.312C551.526 787.872 508.873 695.888 449.08 701.786C378.173 708.782 339.24 816.731 268.405 809.04C206.467 802.315 166.816 728.993 149.214 669.23C132.425 612.23 170.864 555.279 176.233 496.102C181.011 443.444 160.525 389.726 179.064 340.208C198.97 287.043 235.435 239.528 283.565 209.422C332.396 178.879 393.18 157.437 449.08 171.313Z"
            fill="#14EDF4"
          />
        </g>
        <defs>
          <filter
            id="filter0_f_340_13067"
            x="-330"
            y="-133"
            width="871.574"
            height="841.747"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="50"
              result="effect1_foregroundBlur_340_13067"
            />
          </filter>
          <filter
            id="filter1_f_340_13067"
            x="-44.9999"
            y="-252"
            width="810.634"
            height="748.255"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="50"
              result="effect1_foregroundBlur_340_13067"
            />
          </filter>
          <filter
            id="filter2_f_340_13067"
            x="45.0001"
            y="67"
            width="794.623"
            height="842.432"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="50"
              result="effect1_foregroundBlur_340_13067"
            />
          </filter>
        </defs>
      </svg>

      <img
        src={map}
        alt="map"
        className={`PingMapsButton__BgImage ${isHovered ? "PingMapsButton__BgImage--Hovered" : ""}`}
      />
    </a>
  );
};

export default PingMapsButton;
